import {
  LayoutDashboard,
  Folder,
  FileText,
  Files,
  Share2,
  Video,
  History,
  ListOrdered,
  FolderTree,
  Layers,
  GitBranch,
  Settings,
  Building2,
  UserCog,
  UserCircle,
  HelpCircle,
  FileQuestion,
  Play,
  ClipboardList,
  MessageSquare,
  Bug,
  Flag,
  BarChart,
  Database,
} from 'lucide-react';
import { getLastTestCaseForHistory } from '../utils/testCaseHistoryNav';

/**
 * Nav items that open as workspace tabs in Electron/package mode.
 * Test Cases uses the same sub-items as the project sidebar accordion.
 */
export const workspaceSidebarItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'projects', label: 'All Projects', icon: Folder, path: '/projects' },
  {
    id: 'testCases',
    label: 'Test Cases',
    icon: FileText,
    path: '/test-cases',
    subItems: [
      { tabKey: '/test-cases', label: 'All Test', icon: Files, path: '/test-cases' },
      { tabKey: '/shared-steps', label: 'Shared Steps', icon: Share2, path: '/shared-steps' },
      {
        tabKey: '/screen-recorder',
        label: 'Screen Recorder',
        icon: Video,
        path: '/screen-recorder',
      },
      {
        tabKey: '/test-cases/history',
        label: 'History',
        icon: History,
        path: '/test-cases/history',
        historyTab: 'default',
      },
      {
        tabKey: '/test-cases/history?tab=test_step',
        label: 'Test step history',
        icon: ListOrdered,
        path: '/test-cases/history',
        historyTab: 'test_step',
      },
    ],
  },
  { id: 'suites', label: 'Suites', icon: FolderTree, path: '/suites' },
  { id: 'baselines', label: 'Baselines', icon: Layers, path: '/baselines' },
  { id: 'branches', label: 'Branches', icon: GitBranch, path: '/branches' },
  { id: 'ai-assistant', label: 'AI Assistant', icon: MessageSquare, path: '/ai-assistant' },
  { id: 'test-runs', label: 'Test Runs & Result', icon: Play, path: '/test-runs' },
  { id: 'test-plans', label: 'Test Plans', icon: ClipboardList, path: '/test-plans' },
  { id: 'defects', label: 'Defects', icon: Bug, path: '/defects' },
  { id: 'milestone', label: 'Milestone', icon: Flag, path: '/milestone' },
  { id: 'reports', label: 'Reports', icon: BarChart, path: '/reports' },
  { id: 'data-management', label: 'Data Management', icon: Database, path: '/data-management' },
  { id: 'project-settings', label: 'Project Settings', icon: Settings, path: '/project-settings' },
  { id: 'clients', label: 'All Clients', icon: Building2, path: '/clients' },
  { id: 'users', label: 'User Management', icon: UserCog, path: '/users' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' },
  { id: 'my-account', label: 'My Account', icon: UserCircle, path: '/my-account' },
  { id: 'documentation', label: 'Documentation', icon: FileQuestion, path: '/documentation' },
  { id: 'support', label: 'Support', icon: HelpCircle, path: '/support' },
];

function registerTab(allTabs, entry) {
  const tabKey = entry.tabKey || entry.path;
  allTabs[tabKey] = {
    label: entry.label,
    icon: entry.icon,
    path: entry.path,
    tabKey,
    historyTab: entry.historyTab,
  };
}

/** @returns {Record<string, { label: string, icon: import('react').ComponentType, path: string, tabKey: string, historyTab?: string }>} */
export function getAllWorkspaceTabs() {
  const allTabs = {};

  workspaceSidebarItems.forEach((item) => {
    registerTab(allTabs, item);

    if (item.subItems) {
      item.subItems.forEach((subItem) => registerTab(allTabs, subItem));
    }
  });

  return allTabs;
}

const PROJECT_CONTEXT_PREFIXES = [
  '/dashboard',
  '/test-cases',
  '/test-runs',
  '/test-plans',
  '/review-test-cases',
  '/shared-steps',
  '/screen-recorder',
  '/suites',
  '/baselines',
  '/branches',
  '/project-settings',
  '/test-run-execution',
  '/data-management',
  '/defects',
  '/ai-assistant',
];

export function isProjectContextPath(pathname) {
  return PROJECT_CONTEXT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function copyProjectSearchParams(location, { includeSuiteId = false } = {}) {
  const currentParams = new URLSearchParams(location.search);
  const params = new URLSearchParams();
  const projectId = currentParams.get('project_id');
  const projectName = currentParams.get('project_name');
  const suiteId = currentParams.get('suite_id');

  if (projectId) params.set('project_id', projectId);
  if (projectName) params.set('project_name', projectName);
  if (includeSuiteId && suiteId) params.set('suite_id', suiteId);

  return params;
}

/**
 * Build navigation target for a tab, preserving project/settings query context.
 * @param {string} tabKey
 * @param {{ pathname: string, search: string }} location
 */
export function buildWorkspaceTabUrl(tabKey, location) {
  const allTabs = getAllWorkspaceTabs();
  const tab = allTabs[tabKey];
  if (!tab) return tabKey.split('?')[0];

  const currentParams = new URLSearchParams(location.search);
  const projectId = currentParams.get('project_id');

  if (tab.historyTab === 'test_step') {
    const params = copyProjectSearchParams(location);
    params.set('tab', 'test_step');
    return `/test-cases/history?${params.toString()}`;
  }

  if (tab.path === '/test-cases/history' && tab.historyTab === 'default') {
    const params = copyProjectSearchParams(location);
    params.delete('tab');
    const lastId = getLastTestCaseForHistory();
    if (lastId && projectId) {
      return `/test-cases/history/${lastId}?${params.toString()}`;
    }
    return `/test-cases/history?${params.toString()}`;
  }

  if (projectId && isProjectContextPath(tab.path)) {
    const params = copyProjectSearchParams(location, {
      includeSuiteId: tab.path === '/test-cases',
    });
    const qs = params.toString();
    return qs ? `${tab.path}?${qs}` : tab.path;
  }

  if (tab.path === '/settings') {
    const clientId = currentParams.get('client_id');
    if (clientId) {
      return `/settings?client_id=${encodeURIComponent(clientId)}&section=details`;
    }
  }

  return tab.path;
}

/**
 * Resolve active tab key from current route (pathname + search).
 * @param {{ pathname: string, search: string }} location
 */
export function resolveActiveWorkspaceTabKey(location, allTabs) {
  const { pathname } = location;
  const params = new URLSearchParams(location.search);

  if (pathname.startsWith('/test-cases/history/steps')) {
    return '/test-cases/history?tab=test_step';
  }

  if (pathname.startsWith('/test-cases/history')) {
    if (params.get('tab') === 'test_step') {
      return '/test-cases/history?tab=test_step';
    }
    return '/test-cases/history';
  }

  if (
    pathname.startsWith('/test-cases') &&
    !pathname.startsWith('/test-cases/history')
  ) {
    return '/test-cases';
  }

  if (allTabs[pathname]) return pathname;

  const registeredPaths = Object.keys(allTabs)
    .map((key) => allTabs[key].path)
    .filter((path, index, arr) => arr.indexOf(path) === index)
    .sort((a, b) => b.length - a.length);

  for (const path of registeredPaths) {
    if (path === '/dashboard' || path.startsWith('/test-cases')) continue;
    if (pathname === path || pathname.startsWith(`${path}/`)) {
      const tabKeysForPath = Object.keys(allTabs).filter((k) => allTabs[k].path === path);
      const exactOther = tabKeysForPath.find((key) => pathname === key.split('?')[0]);
      if (exactOther) return exactOther;
      const primaryKey = tabKeysForPath.find((k) => !k.includes('?')) || tabKeysForPath[0];
      if (primaryKey) return primaryKey;
    }
  }

  return null;
}

/** Map a sidebar child item to its workspace tab key. */
export function resolveTabKeyFromSidebarChild(child) {
  if (!child?.path) return null;
  if (child.historyTab === 'test_step') {
    return '/test-cases/history?tab=test_step';
  }
  if (child.path === '/test-cases/history') {
    return '/test-cases/history';
  }
  return child.tabKey || child.path;
}

/** Map a top-level sidebar nav item to a workspace tab key. */
export function resolveTabKeyFromSidebarItem(item) {
  if (!item?.path) return null;
  return item.tabKey || item.path;
}

/** @deprecated Use resolveActiveWorkspaceTabKey */
export function resolveActiveWorkspaceTabPath(pathname, allTabs) {
  return resolveActiveWorkspaceTabKey({ pathname, search: '' }, allTabs);
}
